import React, { useState, useEffect } from 'react';
import { Menu, X, Globe, User, LogOut } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { t as tr } from '../contexts/translations';
import logo from '../assets/logo6.png';

const Navbar = ({ language, setLanguage }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const { currentUser, logout, isAdmin } = useAuth();
  const isAuthenticated = !!currentUser;
  const navigate = useNavigate();
  const T = (key, fallback) => {
    const v = tr(key, language);
    return v === key ? fallback : v;
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setShowLogoutModal(false);
  };

  const toggleMobileMenu = () => {
    setIsOpen(!isOpen);
  };

  const renderAuthButton = () => {
    if (!isAuthenticated) {
      return (
        <Link 
          to="/login" 
          className="group relative overflow-hidden px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-orange-500 to-red-600 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all duration-300 hover:scale-105 shadow-lg"
        >
          <span className="relative z-10 flex items-center gap-2">
            <User className="w-4 h-4" />
            {T('loginButton', 'Connexion')}
          </span>
          <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </Link>
      );
    }
    
    return (
      <div className="flex items-center gap-3">
        <Link 
          to="/profile" 
          className="relative group"
        >
          <div className="w-10 h-10 rounded-full border-2 border-white/30 group-hover:border-white/70 transition-all duration-300 overflow-hidden shadow-lg group-hover:shadow-xl group-hover:scale-110">
            {currentUser?.profileImage ? (
              <img
                src={`https://nouzhaback-theta.vercel.app${currentUser.profileImage}`}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling && e.target.nextSibling.style) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center text-white text-sm font-bold">
                {currentUser?.username ? currentUser.username.charAt(0).toUpperCase() : 'U'}
              </div>
            )}
          </div>
          <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></span>
        </Link>
        {isAdmin() && (
          <Link 
            to="/admin" 
            className={`group relative overflow-hidden px-4 py-2 text-sm font-semibold transition-all duration-300 hover:scale-105 shadow-lg rounded-xl ${
              scrolled 
                ? 'text-white bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' 
                : 'text-amber-600 bg-white/90 hover:bg-white hover:text-amber-700'
            }`}
          >
            <span className="relative z-10 flex items-center gap-2">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              {T('adminLabel', 'Admin')}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>
        )}
        <button 
          onClick={() => setShowLogoutModal(true)} 
          className={`group relative p-2 rounded-full transition-all duration-300 hover:scale-110 ${
            scrolled 
              ? 'bg-red-50 text-red-600 hover:bg-red-100' 
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
        >
          <LogOut className="w-5 h-5" />
        </button>
      </div>
    );
  };

  return (
    <>
      <AnimatePresence>
        {showLogoutModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center"
            onClick={() => setShowLogoutModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: -20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: -20 }}
              className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-gray-800 mb-4">{T('logoutConfirmTitle', 'Confirmation')}</h2>
              <p className="text-gray-600 mb-8">{T('logoutConfirmMessage', 'Êtes-vous sûr de vouloir vous déconnecter ?')}</p>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowLogoutModal(false)}
                  className="px-6 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300"
                >
                  {T('cancel', 'Annuler')}
                </button>
                <button
                  onClick={handleLogout}
                  className="px-6 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  {T('logout', 'Déconnexion')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <header 
        className={`fixed z-[60] transition-all duration-500 ease-out ${
          scrolled 
            ? 'w-full bg-white shadow-xl border-b border-orange-200/50 top-0' 
            : 'w-[calc(100%-2rem)] left-4 right-4 bg-gradient-to-r from-amber-500 to-amber-600 border border-orange-200/60 shadow-2xl mt-6 rounded-2xl'
        }`}
      >
        <div className={`container mx-auto flex justify-between items-center transition-all duration-500 ease-out ${
          scrolled ? 'max-w-7xl px-6' : 'px-6'
        }`}>
          <Link to="/" className="flex items-center group">
            <img 
              src={logo} 
              alt="PalaisTouristiques" 
              className="h-16 w-auto transition-all duration-500"
            />
            {/* Optional: Keep text as fallback or remove completely */}
            {/* <span className={`text-xl font-bold transition-colors duration-500 ${
              scrolled ? 'text-gray-800' : 'text-white'
            }`}>
              PalaisTouristiques
            </span> */}
          </Link>

          <nav className="hidden md:flex items-center space-x-1">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                `group relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? scrolled 
                      ? 'text-orange-600 bg-orange-50 font-semibold' 
                      : 'text-white bg-white/20 font-semibold'
                    : scrolled
                      ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{T('home', 'Accueil')}</span>
                  {isActive && (
                    <motion.div 
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full ${
                        scrolled ? 'bg-orange-500' : 'bg-white'
                      }`}
                      layoutId="activeTab"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            <NavLink 
              to="/services" 
              className={({ isActive }) => 
                `group relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? scrolled 
                      ? 'text-orange-600 bg-orange-50 font-semibold' 
                      : 'text-white bg-white/20 font-semibold'
                    : scrolled
                      ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{T('services', 'Services')}</span>
                  {isActive && (
                    <motion.div 
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full ${
                        scrolled ? 'bg-orange-500' : 'bg-white'
                      }`}
                      layoutId="activeTab"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            
            <NavLink 
              to="/contact" 
              className={({ isActive }) => 
                `group relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? scrolled 
                      ? 'text-orange-600 bg-orange-50 font-semibold' 
                      : 'text-white bg-white/20 font-semibold'
                    : scrolled
                      ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{T('navContact', 'Contact')}</span>
                  {isActive && (
                    <motion.div 
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full ${
                        scrolled ? 'bg-orange-500' : 'bg-white'
                      }`}
                      layoutId="activeTab"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            <NavLink 
              to="agency-partnership" 
              className={({ isActive }) => 
                `group relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? scrolled 
                      ? 'text-orange-600 bg-orange-50 font-semibold' 
                      : 'text-white bg-white/20 font-semibold'
                    : scrolled
                      ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{T('navPro', 'Pro')}</span>
                  {isActive && (
                    <motion.div 
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full ${
                        scrolled ? 'bg-orange-500' : 'bg-white'
                      }`}
                      layoutId="activeTab"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </>
              )}
            </NavLink>
            
            <NavLink 
              to="/circuit-sur-mesure" 
              className={({ isActive }) => 
                `group relative px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                  isActive
                    ? scrolled 
                      ? 'text-orange-600 bg-orange-50 font-semibold' 
                      : 'text-white bg-white/20 font-semibold'
                    : scrolled
                      ? 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className="relative z-10">{T('navCustomCircuit', 'Circuit sur mesure')}</span>
                  {isActive && (
                    <motion.div 
                      className={`absolute bottom-0 left-0 right-0 h-0.5 rounded-full ${
                        scrolled ? 'bg-orange-500' : 'bg-white'
                      }`}
                      layoutId="activeTab"
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          </nav>

          <div className="hidden md:flex items-center space-x-4">
            <button 
              onClick={() => setLanguage(language === 'en' ? 'fr' : 'en')}
              className={`flex items-center space-x-2 px-3 py-2 rounded-xl font-medium transition-all duration-300 ${
                scrolled 
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm">{language === 'en' ? 'EN' : 'FR'}</span>
            </button>
            {renderAuthButton()}
          </div>

          <button 
            className={`group relative overflow-hidden md:hidden p-3 rounded-xl transition-all duration-300 hover:scale-105 ${
              scrolled 
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' 
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
            onClick={toggleMobileMenu}
          >
            <span className="relative z-10">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </span>
          </button>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div 
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="md:hidden bg-white border border-orange-200/50 shadow-2xl absolute top-full left-0 right-0 py-6 mt-3 mx-4 rounded-2xl z-[60]"
            >
              <div className="container flex flex-col space-y-4 px-6">
                <NavLink 
                  to="/" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                      isActive 
                        ? 'text-orange-600 bg-orange-50 border border-orange-200' 
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`
                  } 
                  onClick={toggleMobileMenu}
                >
                  {T('home', 'Accueil')}
                </NavLink>
                <NavLink 
                  to="/services" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                      isActive 
                        ? 'text-orange-600 bg-orange-50 border border-orange-200' 
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`
                  } 
                  onClick={toggleMobileMenu}
                >
                  {T('services', 'Services')}
                </NavLink>
                <NavLink 
                  to="/contact" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                      isActive 
                        ? 'text-orange-600 bg-orange-50 border border-orange-200' 
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`
                  } 
                  onClick={toggleMobileMenu}
                >
                  {T('navContact', 'Contact')}
                </NavLink>
                <NavLink 
                  to="agency-partnership" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                      isActive 
                        ? 'text-orange-600 bg-orange-50 border border-orange-200' 
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`
                  } 
                  onClick={toggleMobileMenu}
                >
                  {T('navPro', 'Pro')}
                </NavLink>
                <NavLink 
                  to="/circuit-sur-mesure" 
                  className={({ isActive }) => 
                    `block px-4 py-3 text-sm font-medium rounded-xl transition-all ${
                      isActive 
                        ? 'text-orange-600 bg-orange-50 border border-orange-200' 
                        : 'text-gray-700 hover:text-orange-600 hover:bg-orange-50'
                    }`
                  } 
                  onClick={toggleMobileMenu}
                >
                  {T('navCustomCircuit', 'Circuit sur mesure')}
                </NavLink>
                <div className="pt-4 border-t border-orange-200">
                  <div className="flex flex-col space-y-3">
                    <button 
                      onClick={() => {
                        setLanguage(language === 'en' ? 'fr' : 'en');
                        toggleMobileMenu();
                      }}
                      className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                    >
                      <Globe className="w-4 h-4" />
                      {language === 'en' ? T('langEnglish', 'English') : T('langFrench', 'Français')}
                    </button>
                    {!isAuthenticated ? (
                      <Link 
                        to="/login" 
                        className="px-4 py-3 text-center font-medium text-white bg-gradient-to-r from-orange-500 to-red-600 rounded-xl hover:from-orange-600 hover:to-red-700 transition-all shadow-lg" 
                        onClick={toggleMobileMenu}
                      >
                        {T('loginButton', 'Connexion')}
                      </Link>
                    ) : (
                      <>
                        <Link 
                          to="/profile" 
                          className="flex items-center gap-3 px-4 py-3 text-center font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all" 
                          onClick={toggleMobileMenu}
                        >
                          <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-300">
                            {currentUser?.profileImage ? (
                              <img
                                src={`https://nouzhaback-theta.vercel.app${currentUser.profileImage}`}
                                alt="Profile"
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  if (e.target.nextSibling && e.target.nextSibling.style) {
                                    e.target.nextSibling.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-r from-orange-500 to-red-600 flex items-center justify-center text-white text-sm font-bold">
                                {currentUser?.username ? currentUser.username.charAt(0).toUpperCase() : 'U'}
                              </div>
                            )}
                          </div>
                          {currentUser?.username || T('profileLabel', 'Profil')}
                        </Link>
                        <button 
                          onClick={() => setShowLogoutModal(true)} 
                          className="flex items-center justify-center gap-2 px-4 py-3 font-medium text-red-600 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-all"
                        >
                          <LogOut className="w-4 h-4" />
                          {T('logout', 'Déconnexion')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};

export default Navbar;